// import AdminLayout from "../../../Layout/AdminLayout";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import PersonalDetails from "./modules/PersonalDetails";
import { CardComp, ButtonComp } from "../../../components";
import { useViewMemberQuery, useUpdateMemberMutation } from "../../../store/admin/memberManagement/memberSlice";
import { useGetOccupationsQuery } from "../../../store/selectableSlice";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useMemo, useCallback } from "react";
import uploadImage from "../../../utils/uploadImage";
import { isValidPhoneFormat } from "../../../utils/phoneUtils";
import { PageTitleWithNav } from "../modules/PageHeaders";
import Loader from "../../../components/Ui/Loader";
import { toast } from "react-hot-toast";

const UpdateMember = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  // Skip refetch on error to prevent form reset
  const { data: memberData = {}, isFetching } = useViewMemberQuery(id);
  const { data: occupations = [] } = useGetOccupationsQuery();
  const [updateMember] = useUpdateMemberMutation();
  const [imgFile, setImgFile] = useState();
  // What the live membership number check last found (see PersonalDetails)
  const [memberNoState, setMemberNoState] = useState({ status: "idle", isChecking: false });
  const handleMemberNoState = useCallback((state) => setMemberNoState(state), []);
  // A taken number, or one still being checked, must not be saved. If the check could not
  // run (connection), saving is allowed: the server checks again and refuses a duplicate.
  const memberNoBlocksSave = memberNoState.isChecking || memberNoState.status === "taken";
  const [errorImg, setErrorImg] = useState("Click to upload image");

  const updateImgFile = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setImgFile(null);
      return setErrorImg("Upload an image");
    }
    const size = file?.size / 1024 / 1024;
    const type = file?.type?.split("/")[1];
    const validTypes = ["jpeg", "jpg", "png"];
    if (size > 10 || !validTypes.includes(type)) {
      setErrorImg(
        "Invalid. File size must not be more than 10mb\nFile must me either jpg jpeg png"
      );
    } else {
      setErrorImg(null);
      setImgFile(file);
    }
  };

  // Validation schema using Yup
  // For dropdown fields, we need to check for empty strings, not just undefined/null
  const validationSchema = Yup.object({
    first_name: Yup.string()
      .trim()
      .required("First name is required"),
    last_name: Yup.string()
      .trim()
      .required("Last name is required"),
    // Occupation validation: either occupation_id is selected OR occupation is filled (for "Others")
    occupation_id: Yup.string()
      .required("Occupation is required")
      .test("not-empty", "Please select an occupation", (value) => {
        return value && value.toString().trim().length > 0 && value !== "";
      }),
    occupation: Yup.string()
      .trim()
      .when("occupation_id", {
        is: (val) => val === "others",
        then: (schema) => schema
          .required("Please specify your occupation")
          .test("not-empty", "Please specify your occupation", (value) => {
            return value && value.trim().length > 0;
          }),
        otherwise: (schema) => schema.required("Occupation is required"),
      }),
    dob: Yup.string()
      .required("Date of birth is required")
      .test("not-empty", "Date of birth is required", (value) => {
        return value && value.trim().length > 0;
      }),
    address: Yup.string()
      .trim()
      .required("Address is required")
      .test("not-empty", "Address is required", (value) => {
        return value && value.trim().length > 0;
      }),
    marital_id: Yup.string()
      .required("Marital status is required")
      .test("not-empty", "Please select a marital status", (value) => {
        return value && value.toString().trim().length > 0 && value !== "";
      }),
    next_of_kin_name: Yup.string()
      .trim()
      .required("Next of kin name is required")
      .test("not-empty", "Next of kin name is required", (value) => {
        return value && value.trim().length > 0;
      }),
    // The phone input and the API both use the international form (+2348012345678), so check
    // it the same way the registration form does, not as exactly 11 digits.
    next_of_kin_phone: Yup.string()
      .trim()
      .required("Next of kin phone number is required")
      .test("valid-phone", "Enter a valid phone number, like 0801 234 5678", (value) =>
        isValidPhoneFormat(value, "234")
      ),
    relationship_status_id: Yup.string()
      .required("Relationship status is required")
      .test("not-empty", "Please select a relationship status", (value) => {
        return value && value.toString().trim().length > 0 && value !== "";
      }),
    gender_id: Yup.string()
      .required("Gender is required")
      .test("not-empty", "Please select a gender", (value) => {
        return value && value.toString().trim().length > 0 && value !== "";
      }),
    title_id: Yup.string()
      .required("Title is required")
      .test("not-empty", "Please select a title", (value) => {
        return value && value.toString().trim().length > 0 && value !== "";
      }),
    phone: Yup.string()
      .trim()
      .required("Phone number is required")
      .test("valid-phone", "Enter a valid phone number, like 0801 234 5678", (value) =>
        isValidPhoneFormat(value, "234")
      ),
    email: Yup.string()
      .trim()
      .email("Please enter a valid email address")
      .required("Email address is required")
      .test("not-empty", "Email address is required", (value) => {
        return value && value.trim().length > 0;
      }),
    member_no: Yup.string()
      .trim()
      .required("Member number is required")
      .test("not-empty", "Member number is required", (value) => {
        return value && value.trim().length > 0;
      }),
  });

  // Memoize initial values
  // Map occupation name to occupation_id for dropdown selection
  const initialValues = useMemo(() => {
    let occupationId = memberData?.occupation_id || "";
    const occupationName = memberData?.occupation || "";
    
    // If occupation_id is not provided but occupation name is, find the matching ID
    if (!occupationId && occupationName && occupations.length > 0) {
      const matchingOccupation = occupations.find(
        (occ) => occ.name?.toLowerCase().trim() === occupationName.toLowerCase().trim()
      );
      if (matchingOccupation) {
        // Found matching occupation in the list
        occupationId = matchingOccupation.id.toString();
      } else if (occupationName.trim().length > 0) {
        // Occupation name exists but not in the list - it's a custom "Others" occupation
        occupationId = "others";
      }
    }

    return {
      first_name: memberData?.first_name || "",
      last_name: memberData?.last_name || "",
      other_name: memberData?.other_name || "",
      member_no: memberData?.member_no || "",
      phone: memberData?.phone || "",
      photo: memberData?.photo || "",
      email: memberData?.email || "",
      member_id: memberData?.member_id || "",
      address: memberData?.address || "",
      marital_id: memberData?.marital_id || "",
      gender_id: memberData?.gender_id || "",
      title_id: memberData?.title_id || "",
      dob: memberData?.dob || "",
      occupation: occupationName,
      occupation_id: occupationId, // Mapped from occupation name if needed
      next_of_kin_name: memberData?.next_of_kin_name || "",
      next_of_kin_phone: memberData?.next_of_kin_phone || "",
      relationship_status_id: memberData?.relationship_status_id || "",
    };
  }, [memberData, occupations]);

  const handleFormSubmit = async (formikValues, { setSubmitting }) => {
    if (memberNoBlocksSave) {
      toast.error(
        memberNoState.isChecking
          ? "Please wait while we check the membership number"
          : "That membership number is already used by another member"
      );
      setSubmitting(false);
      return;
    }

    // Exclude occupation_id from backend payload (only used for form UI)
    const { occupation_id, ...formData } = formikValues;
    
    const reqData = {
      ...formData,
      id: id,
      branch_id: memberData?.branch_id,
    };
    if (imgFile) {
      reqData["photo"] = await uploadImage(imgFile);
    }
    
    const toastId = toast.loading("Updating member...");
    try {
      const response = await updateMember(reqData);
      
      if (response.error) {
        toast.error(response.error?.data?.message || "Failed to update member", { id: toastId });
        setSubmitting(false);
        return;
      }
      
      if (response.data?.status === false) {
        toast.error(response.data?.message || "Failed to update member", { id: toastId });
        setSubmitting(false);
        return;
      }
      
      toast.success(response.data?.message || "Member updated successfully", { id: toastId });
      // Navigate back to profile page after successful update
      navigate(`/super_admin/member_management/members/${id}`);
    } catch (error) {
      toast.error("An error occurred while updating member", { id: toastId });
      console.error("Failed to update member:", error);
      setSubmitting(false);
    }
  };
  return (
    <section className="px-lg-3 min-vh-100 pb-5">
      {isFetching ? (
        <div className="bg-white">
          <Loader />
        </div>
      ) : (
        <Formik
          key={id}
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          <Form>
            <PageTitleWithNav
              title={"Edit"}
              highlightText={`${memberData?.first_name || ""} ${
                memberData?.last_name || ""
              }`}
              action={() => navigate(-1)}
            />

            <CardComp>
              <div className="py-4"></div>
              <PersonalDetails
                imgFile={imgFile}
                errorImg={errorImg}
                updateImgFile={updateImgFile}
                originalMemberNo={memberData?.member_no || ""}
                onMemberNoAvailabilityChange={handleMemberNoState}
              />
            </CardComp>
            <div className="">
              <ButtonComp
                btnText={"Proceed"}
                btnClassName={
                  "bg-primary1 py-2 px-4 text-white fw-semibold border-0 rounded"
                }
                type={"submit"}
                disabled={memberNoBlocksSave}
              />
            </div>
          </Form>
        </Formik>
      )}
    </section>
  );
};

export default UpdateMember;
